/**
 * OAuth 2.0 Utilities
 * 
 * Provides PKCE (Proof Key for Code Exchange) and state management
 * for secure OAuth authentication flows.
 */

import { UserType } from '../constants/userTypes';

export interface OAuthStateData {
  userType?: UserType;
  tosData?: {
    version: string;
    acceptedAt: string;
    marketingConsent: boolean;
  };
  codeVerifier: string;
  state: string;
  redirectPath?: string;
  expiresAt: number;
  /**
   * Explicit provider tag. Set for non-Cognito providers (Naver, Kakao)
   * so the callback handler can route to the right backend mutation
   * without relying on the (now-ambiguous) "empty codeVerifier" sentinel.
   * Cognito-backed flows (Google, etc.) leave this undefined.
   */
  provider?: 'naver' | 'kakao';
}

const OAUTH_STATE_KEY = 'oauth_state';
const STATE_EXPIRATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Generates a cryptographically secure random string
 * @param length Length of the string (default: 43)
 * @returns Base64URL-encoded random string
 */
function generateRandomString(length: number = 43): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Encodes a byte array to Base64URL format
 * @param buffer Byte array to encode
 * @returns Base64URL-encoded string
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...Array.from(buffer)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generates SHA-256 hash of a string
 * @param plain Plain text to hash
 * @returns Base64URL-encoded hash
 */
async function sha256(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
}

/**
 * Generates a PKCE code verifier
 * @returns Random string (43-128 characters)
 */
export function generateCodeVerifier(): string {
  return generateRandomString(43);
}

/**
 * Generates a PKCE code challenge from a code verifier
 * @param verifier Code verifier
 * @returns SHA-256 hash of the verifier
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  return sha256(verifier);
}

/**
 * Generates a random state parameter for CSRF protection
 * @returns Random string (32 characters)
 */
export function generateState(): string {
  return generateRandomString(32);
}

/**
 * Stores OAuth state data in session storage
 * @param stateData OAuth state data to store
 */
export function storeOAuthState(stateData: Omit<OAuthStateData, 'expiresAt'>): void {
  const dataWithExpiration: OAuthStateData = {
    ...stateData,
    expiresAt: Date.now() + STATE_EXPIRATION_MS,
  };
  
  sessionStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(dataWithExpiration));
}

/**
 * Retrieves OAuth state data from session storage
 * @returns OAuth state data or null if not found or expired
 */
export function retrieveOAuthState(): OAuthStateData | null {
  const stored = sessionStorage.getItem(OAUTH_STATE_KEY);
  
  if (!stored) {
    return null;
  }
  
  try {
    const data: OAuthStateData = JSON.parse(stored);
    
    // Check if state has expired
    if (Date.now() > data.expiresAt) {
      clearOAuthState();
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Failed to parse OAuth state:', error);
    clearOAuthState();
    return null;
  }
}

/**
 * Clears OAuth state data from session storage
 */
export function clearOAuthState(): void {
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Validates a state parameter against stored state
 * @param receivedState State parameter from OAuth callback
 * @returns True if state is valid, false otherwise
 */
export function validateState(receivedState: string): boolean {
  const storedState = retrieveOAuthState();
  
  if (!storedState) {
    console.error('No stored OAuth state found');
    return false;
  }
  
  if (storedState.state !== receivedState) {
    console.error('State parameter mismatch');
    return false;
  }
  
  return true;
}
